import React, { useState, useEffect, useContext } from "react";
import "./CreateFlightPlan.css";
import { useNavigate } from "react-router-dom";
import CreateFlightPlanHeader from "./Views/CreateFlightPlanHeader";
import CreateFlightPlanTable from "./Tables/CreateFlightPlanTable";
import { useMutation } from "@tanstack/react-query";
import useAirlineAdminApi from "../../../../services/api/useAirlineAdminApi";
import OvertimeDialog from "../../../../components/element/OvertimeDialog";
import SnackbarAlert from "../../../../components/element/SnackbarAlert";
import ErrorSnackbarAlert from "../../../../components/element/ErrorSnackbarAlert";
import ROUTE_PATHS from "../../../../utils/routePaths";
import { StorageContext } from "../../../../services/StorageProvider";
import { useFormData } from "../../../../helpers/FormDataProvider";
import { AirportType } from "../../../../utils/constants";
import dayjs from "dayjs";

const CreateFlightPlan = () => {
  const navigate = useNavigate();
  const { userData } = useFormData();
  const { associatedAirlineData, userProfileData } = useContext(StorageContext);
  const [showOvertimeDialog, setShowOvertimeDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [selectedAirline, setSelectedAirline] = useState({});
  const [selectedOrigin, setSelectedOrigin] = useState({});
  const [selectedDestination, setSelectedDestination] = useState({});
  const { checkOvertimeFee, createCommercialFlightPlan } = useAirlineAdminApi(
    userData?.isAAUser
  );

  const [flightDetails, setFlightDetails] = useState({
    userId: "",
    airlineId: "",
    aircraftTypeId: "",
    iataFlightNumber: "",
    icaoFlightNumber: "",
    flightNumber: "",
    aircraftRegistrationNumber: "",
    departureAirportId: "",
    arrivalAirportId: "",
    departureTime: "",
    arrivalTime: "",
    departureTimeFromArrivalAirport: "",
    requiresArrivalOvertime: false,
    requiresDepartureOvertime: false,
  });

  const commercialFlightPlanMutation = useMutation({
    mutationFn: ({ data }) => createCommercialFlightPlan(data),
    onSuccess: () => {
      navigate(ROUTE_PATHS.COMMERCIAL_AIRLINE_FLIGHT_ROSTER);
    },
  });

  const checkOvertimeMutation = useMutation({
    mutationFn: ({ data }) => checkOvertimeFee(data),
    onSuccess: (response) => {
      const updatedFlightDetails = {
        ...flightDetails,
        requiresArrivalOvertime: response?.data?.requiresArrivalOvertime,
        requiresDepartureOvertime: response?.data?.requiresDepartureOvertime,
      };
      setFlightDetails(updatedFlightDetails);
      if (
        (response?.data?.requiresArrivalOvertime &&
          selectedDestination.airportType === AirportType.DOMESTIC) ||
        (response?.data?.requiresDepartureOvertime &&
          selectedOrigin.airportType === AirportType.DOMESTIC)
      ) {
        setShowOvertimeDialog(true);
      } else {
        commercialFlightPlanMutation.mutate({
          data: flightDetails,
        });
      }
    },
  });

  useEffect(() => {
    setFlightDetails({
      ...flightDetails,
      userId: userProfileData.id,
    });

    if (!userData?.isAAUser && associatedAirlineData?.data?.length === 1) {
      setSelectedAirline(associatedAirlineData?.data[0]);
      setFlightDetails({
        ...flightDetails,
        airlineId: associatedAirlineData?.data[0]?.id,
      });
    }
  }, [associatedAirlineData, userData]);

  const handleBackButton = () => {
    navigate(ROUTE_PATHS.COMMERCIAL_AIRLINE_FLIGHT_ROSTER);
  };

  const handleErrorPopupShow = (error) => {
    setShowErrorPopup(true);
    setErrorMessage(error);
  };

  const handleErrorPopupClose = () => {
    setShowErrorPopup(false);
  };

  const handleSaveButton = () => {
    const minAllowedDateTime = dayjs().subtract(1, "month").startOf("month");

    if (selectedOrigin.id === selectedDestination.id) {
      handleErrorPopupShow("Origin and Destination cannot be the same.");
    } else if (
      selectedOrigin.airportType !== AirportType.DOMESTIC &&
      selectedDestination.airportType !== AirportType.DOMESTIC
    ) {
      handleErrorPopupShow("At least one airport type should be domestic.");
    } else if (
      flightDetails.departureTime &&
      flightDetails.arrivalTime &&
      flightDetails.departureTime === flightDetails.arrivalTime
    ) {
      handleErrorPopupShow(
        "Arrival time and Departure time cannot be the same."
      );
    } else if (
      flightDetails.arrivalTime &&
      flightDetails.departureTimeFromArrivalAirport &&
      flightDetails.arrivalTime ===
      flightDetails.departureTimeFromArrivalAirport
    ) {
      handleErrorPopupShow(
        "Arrival time and Departure time from Arrival Airport cannot be the same."
      );
    } else if (dayjs(flightDetails.departureTime).isBefore(minAllowedDateTime)) {
      handleErrorPopupShow(
        "Selected departure time cannot be earlier than the first day of last month."
      );
    } else if (dayjs(flightDetails.arrivalTime).isBefore(minAllowedDateTime)) {
      handleErrorPopupShow(
        "Selected arrival time cannot be earlier than the first day of last month."
      );
    } else if (
      flightDetails.departureTimeFromArrivalAirport &&
      dayjs(flightDetails.departureTimeFromArrivalAirport).isBefore(
        minAllowedDateTime
      )
    ) {
      handleErrorPopupShow(
        "Selected departure time from arrival airport cannot be earlier than the first day of last month."
      );
    } else {
      // If any terminal is selected, skip overtime check and directly create flight plan
      if (flightDetails.departureAirportTerminalId || flightDetails.arrivalAirportTerminalId) {
        commercialFlightPlanMutation.mutate({
          data: flightDetails,
        });
      } else {
        const data = {
          arrivalAirportId: flightDetails.arrivalAirportId,
          departureAirportId: flightDetails.departureAirportId,
          arrivalTime: flightDetails.arrivalTime,
          departureTime: flightDetails.departureTime,
        };
        checkOvertimeMutation.mutate({
          data: data,
        });
      }
    }
  };

  const handleAcceptButton = () => {
    setShowOvertimeDialog(false);
    commercialFlightPlanMutation.mutate({
      data: flightDetails,
    });
  };

  const validateFlightDetails = () => {
    if (
      flightDetails.aircraftTypeId === "" ||
      flightDetails.flightNumber.trim() === "" ||
      flightDetails.departureAirportId === "" ||
      flightDetails.arrivalAirportId === "" ||
      !flightDetails.departureTime ||
      !flightDetails.arrivalTime ||
      !flightDetails.departureTimeFromArrivalAirport
    ) {
      return false;
    }
    return true;
  };

  return (
    <div className="create-flight-plan-container">
      {checkOvertimeMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {checkOvertimeMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      {commercialFlightPlanMutation.isError ? (
        <SnackbarAlert type={"error"}>
          {commercialFlightPlanMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      <CreateFlightPlanHeader
        isValid={validateFlightDetails()}
        onClickBack={handleBackButton}
        onClickSave={handleSaveButton}
        isLoading={commercialFlightPlanMutation.isPending}
      />
      <CreateFlightPlanTable
        flightDetails={flightDetails}
        airlineData={associatedAirlineData}
        setFlightDetails={setFlightDetails}
        selectedAirline={selectedAirline}
        setSelectedAirline={setSelectedAirline}
        selectedOrigin={selectedOrigin}
        setSelectedOrigin={setSelectedOrigin}
        selectedDestination={selectedDestination}
        setSelectedDestination={setSelectedDestination}
      />
      <OvertimeDialog
        flightDetails={flightDetails}
        origin={selectedOrigin}
        destination={selectedDestination}
        open={showOvertimeDialog}
        onClose={() => setShowOvertimeDialog(false)}
        onAccept={handleAcceptButton}
      />
      <ErrorSnackbarAlert open={showErrorPopup} onClose={handleErrorPopupClose}>
        {errorMessage}
      </ErrorSnackbarAlert>
    </div>
  );
};

export default CreateFlightPlan;
